import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, message, Switch } from 'antd';

import { isEmpty as _isEmpty } from 'lodash-es';

import SettingsFooter from '../SettingsFooter';
import { PushNotifications } from '@capacitor/push-notifications';
import { updatePushToken } from '../../../store/actions/userActions';
import { Capacitor } from '@capacitor/core';

const platform = Capacitor.getPlatform();

const NotificationSettings = (props) => {
  const { t } = useTranslation();
  const { me } = useSelector((state) => state.auth);

  const {
    reset,
    control,
    handleSubmit,
    watch,
    formState: { dirtyFields, isDirty },
  } = useForm({
    defaultValues: {
      pushNotificationEnabled: props?.form.pushNotificationEnabled ?? true,
      emailNotificationEnabled: props?.form.emailNotificationEnabled ?? false,
      emailNotificationEmail: props?.form.emailNotificationEmail || me?.email || '',
      whatsappNotificationEnabled: true, // Always true (dummy)
    },
  });

  const watchEmailNotificationEnabled = watch('emailNotificationEnabled', false);

  props.dirtyFieldsRef.current = dirtyFields;

  useEffect(() => {
    reset({
      pushNotificationEnabled: props?.form.pushNotificationEnabled ?? true,
      emailNotificationEnabled: props?.form.emailNotificationEnabled ?? false,
      emailNotificationEmail: props?.form.emailNotificationEmail || me?.email || '',
      whatsappNotificationEnabled: true, // Always true (dummy)
    });
  }, [props.form, reset, me]);

  function handleSave(data) {
    props.handleDispatch(data);
  }

  function handleError(error) {
    message.error(t('formSettings.updateFail'));
  }

  const dispatch = useDispatch();

  const [hasNotificationPermission, setHasNotificationPermission] = React.useState(false);

  // Add effect to check permission status on mount
  useEffect(() => {
    if (platform === 'web') return;
    PushNotifications.checkPermissions().then((res) => {
      setHasNotificationPermission(res.receive === 'granted');
    });
  }, []);

  // Modify the push notification switch controller
  const handlePushNotificationChange = async (checked) => {
    if (checked && !hasNotificationPermission) {
      const permission = await PushNotifications.requestPermissions();
      if (permission.receive === 'granted') {
        setHasNotificationPermission(true);
        // Register for push notifications and get token
        PushNotifications.register();
        PushNotifications.addListener('registration', (token) => {
          if (me?.deviceTokens?.includes(token.value)) return;
          dispatch(updatePushToken(token.value));
        });
        return true;
      } else {
        if (permission.receive === 'denied') {
          message.error(t('formSettings.enableNotificationsMessage'));
        }
        return false;
      }
    }
    return checked;
  };

  const handleEmailNotificationChange = async (checked) => {
    if (checked && !me?.email) {
      message.error(t('formSettings.addEmailMessage'));
      return false;
    }
    return checked;
  };

  return (
    <div className="form-settings-screen">
      <Form
        className="form-settings-inputs"
        colon={false}
        labelCol={{
          span: 8,
        }}
        labelAlign="left"
        requiredMark={false}
      >
        <Controller
          control={control}
          name="whatsappNotificationEnabled"
          render={({ field }) => (
            <Form.Item label={t('formSettings.whatsappNotifications')}>
              <Switch
                className="form-input"
                onChange={() => {}} // Dummy - always enabled
                checked={true}
                disabled={true}
                name={field.name}
                ref={field.ref}
              />
            </Form.Item>
          )}
        />

        <Controller
          control={control}
          name="emailNotificationEnabled"
          render={({ field }) => (
            <Form.Item label={t('formSettings.emailNotifications')}>
              <Switch
                className="form-input"
                onChange={async (checked) => {
                  const newValue = await handleEmailNotificationChange(checked);
                  field.onChange(newValue);
                }}
                checked={field.value}
                name={field.name}
                ref={field.ref}
              />
            </Form.Item>
          )}
        />

        {watchEmailNotificationEnabled && (
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                label={t('formSettings.emailAddress')}
                validateStatus={_isEmpty(error) ? 'success' : 'error'}
                help={!_isEmpty(error) ? error?.message : null}
              >
                <Input {...field} type="email" placeholder={t('formSettings.enterEmailAddress')} />
              </Form.Item>
            )}
            control={control}
            name="emailNotificationEmail"
            rules={{
              required: watchEmailNotificationEnabled
                ? t('formSettings.emailAddressRequired')
                : false,
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('errors.invalidEmail'),
              },
            }}
          />
        )}

        {platform !== 'web' && (
          <Controller
            control={control}
            name="pushNotificationEnabled"
            render={({ field }) => (
              <Form.Item label={t('formSettings.pushNotifications')}>
                <Switch
                  className="form-input"
                  onChange={async (checked) => {
                    const newValue = await handlePushNotificationChange(checked);
                    field.onChange(newValue);
                  }}
                  checked={field.value && hasNotificationPermission}
                  name={field.name}
                  ref={field.ref}
                />
              </Form.Item>
            )}
          />
        )}
      </Form>
      <SettingsFooter
        handleSubmit={handleSubmit(handleSave, handleError)}
        loading={props.isLoading}
        isDirty={isDirty}
      />
    </div>
  );
};

export default NotificationSettings;
